import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getCsrfToken } from "../services/authService";
import "../styles/OrderDetail.css";

export default function OrderDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [order, setOrder] = useState(null);

  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState("");

  useEffect(() => {
    loadOrder();
  }, [id]);

  const loadOrder = async () => {
    const response = await fetch(`/api/orders/${id}`, {
      credentials: "include",
    });

    if (response.ok) {
      setOrder(await response.json());
    } else {
      navigate("/orders");
    }
  };

  const canCancelOrder = () => {
    if (!order) {
      return false;
    }

    if (
      order.status !== "PAID" ||
      order.paymentStatus !== "PAID"
    ) {
      return false;
    }

    const createdAt = new Date(order.createdAt);
    const cancellationDeadline = new Date(
      createdAt.getTime() + 30 * 60 * 1000
    );

    return new Date() <= cancellationDeadline;
  };

  const canRequestReturn = () => {
    if (
      !order ||
      order.status !== "DELIVERED" ||
      !order.deliveredAt
    ) {
      return false;
    }

    const deliveredAt = new Date(order.deliveredAt);
    const deadline = new Date(deliveredAt);

    deadline.setDate(deadline.getDate() + 14);

    return new Date() <= deadline;
  };

  const cancelOrder = async () => {
    if (!window.confirm(t("orders.cancelConfirm"))) {
      return;
    }

    setCancelError("");
    setCancelLoading(true);

    try {
      const csrf = await getCsrfToken();

      const response = await fetch(`/api/orders/${order.id}/cancel`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          [csrf.headerName]: csrf.token,
        },
      });

      if (!response.ok) {
        setCancelError(t("orders.cancelError"));
        return;
      }

      const updatedOrder = await response.json();
      setOrder(updatedOrder);
    } catch {
      setCancelError(t("orders.cancelError"));
    } finally {
      setCancelLoading(false);
    }
  };

  const renderAddress = (address) => {
    if (!address) {
      return <p>{t("orders.noAddress")}</p>;
    }

    return (
      <>
        <p>
          <strong>
            {address.name} {address.lastname1} {address.lastname2 || ""}
          </strong>
        </p>

        <p>
          {address.street}, {address.numberAddress}
          {address.apartment ? `, ${address.apartment}` : ""}
        </p>

        <p>
          {address.zipCode} {address.city}
          {address.province ? `, ${address.province}` : ""}
        </p>

        <p>{address.country}</p>

        {address.phone && (
          <p>
            {t("addresses.phone")}: {address.phone}
          </p>
        )}
      </>
    );
  };

  if (!order) {
    return null;
  }

  return (
    <main className="order-detail-page">
      <section className="order-detail-container">
        <button className="profile-back-btn" onClick={() => navigate("/orders")}>
          ← {t("orders.backToOrders")}
        </button>

        <h1>{order.orderNumber}</h1>

        <button
          type="button"
          className="order-pdf-btn"
          onClick={() => window.open(`/api/orders/${order.id}/pdf`, "_blank")}
        >
          {t("orders.downloadPdf")}
        </button>

        <div className="order-detail-header">
          <div>
            <span>{t("orders.date")}</span>
            <strong>{new Date(order.createdAt).toLocaleDateString()}</strong>
          </div>

          <div>
            <span>{t("orders.statusLabel")}</span>
            <strong>{t(`orders.status.${order.status}`)}</strong>
          </div>

          <div>
            <span>{t("orders.total")}</span>
            <strong>{Number(order.total).toFixed(2)} €</strong>
          </div>
        </div>

        {canCancelOrder() && (
          <section className="order-cancel-user-box">
            <p>{t("orders.cancelInfo")}</p>

            {cancelError && (
              <p className="order-cancel-error">{cancelError}</p>
            )}

            <button
              type="button"
              onClick={cancelOrder}
              disabled={cancelLoading}
            >
              {cancelLoading
                ? t("orders.cancelling")
                : t("orders.cancelOrder")}
            </button>
          </section>
        )}

        {canRequestReturn() && (
          <section className="order-return-user-box">
            <div>
              <h2>{t("returns.availableTitle")}</h2>
              <p>{t("returns.availableText")}</p>
            </div>

            <button
              type="button"
              onClick={() =>
                navigate(`/orders/${order.id}/return`)
              }
            >
              {t("returns.requestButton")}
            </button>
          </section>
        )}

        <section className="order-detail-addresses">
          <article>
            <h2>{t("orders.shippingAddress")}</h2>
            {renderAddress(order.shippingAddress)}
          </article>

          <article>
            <h2>{t("orders.billingAddress")}</h2>
            {renderAddress(order.billingAddress)}
          </article>
        </section>

        <section className="order-payment-info">
          <h2>{t("orders.paymentMethod")}</h2>

          <p>
            <strong>
              {t(`orders.paymentMethods.${order.paymentMethod}`)}
            </strong>
          </p>

          <p>
            {t("orders.paymentStatus")}:{" "}
            <strong>{t(`orders.paymentStatusValues.${order.paymentStatus}`)}</strong>
          </p>
        </section>

        <section className="order-detail-block">
          <h2>{t("orders.products")}</h2>

          <div className="order-detail-items">
            {order.items.map((item) => (
              <article className="order-detail-item" key={item.id}>
                {item.imageUrl && (
                  <img src={item.imageUrl} alt={item.productName} />
                )}

                <div>
                  <h3>{item.productName}</h3>
                  <p>
                    {item.quantity} x {Number(item.unitPrice).toFixed(2)} €
                  </p>
                </div>

                <strong>{Number(item.subtotal).toFixed(2)} €</strong>
              </article>
            ))}
          </div>
        </section>
      </section>
    </main>
  );
}